/**
 * AutoCalculateDatesButton Component
 *
 * Button to automatically calculate contract dates based on child age and kindergarten enrollment
 * Uses the backend API to determine optimal contract start and end dates
 */

import React from 'react';
import { Calculator, Loader2 } from 'lucide-react';
import { useLingui } from '@lingui/react';
import { t, Trans } from '@lingui/macro';

import { Button } from '@/components/ui/button';
import { useAutoCalculateDates } from '@/hooks/contracts/useAutoCalculateDates';

interface AutoCalculateDatesButtonProps {
  /**
   * The child's birthdate as an ISO date string (YYYY-MM-DD)
   * Required for the calculation
   */
  childBirthdate?: string;

  /**
   * The childcare location ID where the child will attend
   * Required for the calculation (used to determine canton)
   */
  childcareId?: string;

  /**
   * Callback when dates are successfully calculated
   * @param startDate - The calculated contract start date
   * @param endDate - The calculated contract end date (null if open-ended)
   */
  onDatesCalculated: (startDate: Date, endDate: Date | null) => void;

  /**
   * Whether the button should be disabled
   * Automatically disabled if no childBirthdate or childcareId is provided
   */
  disabled?: boolean;
}

/**
 * Button component for automatic contract date calculation
 *
 * Features:
 * - Automatic calculation based on child age and kindergarten enrollment
 * - Loading state with spinner animation
 * - Success/error/info toast notifications
 * - Proper i18n support with dynamic locale
 * - Accessibility support with aria-label
 * - Performance optimization with React.memo
 */
export const AutoCalculateDatesButton = React.memo(function AutoCalculateDatesButton({
  childBirthdate,
  childcareId,
  onDatesCalculated,
  disabled = false,
}: AutoCalculateDatesButtonProps) {
  const { i18n } = useLingui();

  // Use the custom hook that handles all validation and calculation logic
  const { isDisabled, isPending, handleClick } = useAutoCalculateDates({
    childBirthdate,
    childcareId,
    onDatesCalculated,
    disabled,
  });

  // Determine button icon
  const ButtonIcon = isPending ? Loader2 : Calculator;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={isDisabled}
      onClick={handleClick}
      aria-label={i18n._(
        t({
          id: "contracts.autoCalc.ariaLabel",
          message: "Calculate contract dates automatically",
        })
      )}
      className="gap-2"
    >
      <ButtonIcon
        className={isPending ? "animate-spin" : undefined}
        aria-hidden="true"
      />
      <span>
        {isPending ? (
          <Trans>Calculating...</Trans>
        ) : (
          <Trans>Auto Calculate</Trans>
        )}
      </span>
    </Button>
  );
});